import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Battery,
  Zap,
  Lock,
  Unlock,
  Play,
  Pause,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Activity,
  Thermometer,
  DollarSign,
  Clock,
  ArrowUp,
  ArrowDown,
  Power,
  Settings,
  ExternalLink,
  Server,
  Shield
} from 'lucide-react';
import { useScanLanguage } from '../contexts/LanguageContext';

interface BatterySlot {
  id: number;
  batteryId?: string;
  soc?: number;
  soh?: number;
  voltage?: number;
  current?: number;
  temperature?: number;
  status: 'empty' | 'inserted' | 'charging' | 'discharging' | 'idle' | 'error';
  doorOpen: boolean;
  safetySensor: boolean;
}

interface BatteryData {
  number: number;
  outputV: number;
  outputA: number;
  outputW: number;
  soc: number;
  soh: number;
  cv: string;
  ccCv: string;
  temperature: number;
  chargingMode: string;
  chargingStatus: string;
  autoChargingMode: boolean;
  acVoltageError: string;
  base: string;
}

interface LogMessage {
  id: string;
  datetime: string;
  device: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
}

interface Transaction {
  hash: string;
  from: string;
  to: string;
  amount: string;
  token: string;
  timestamp: number;
  status: string;
  type: 'charge' | 'payment' | 'transfer';
}

interface Payment {
  id: string;
  amount: number;
  token: string;
  timestamp: number;
  status: string;
  transactionHash?: string;
  description: string;
}

interface AIControlSystemProps {
  stationId: string;
  stationName: string;
  stationType: 'energy' | 'bss';
  onClose: () => void;
}

const AIControlSystem: React.FC<AIControlSystemProps> = ({
  stationId,
  stationName,
  stationType,
  onClose
}) => {
  const { t } = useScanLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'ai' | 'validator'>('ai');
  const [batterySlots, setBatterySlots] = useState<BatterySlot[]>([]);
  const [selectedBattery, setSelectedBattery] = useState<number>(1);
  const [batteryData, setBatteryData] = useState<BatteryData | null>(null);
  const [autoCharging, setAutoCharging] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState<'online' | 'offline'>('online');
  const [ipAddress, setIpAddress] = useState('192.168.1.171');
  const [stationTemperature, setStationTemperature] = useState(32);
  const [activeBatteries, setActiveBatteries] = useState(2);
  const [logs, setLogs] = useState<LogMessage[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(false);
  const [validatorInfo, setValidatorInfo] = useState<any>(null);

  useEffect(() => {
    loadBatteryData();
    loadTransactions();
    loadPayments();
    loadValidatorInfo();
    loadRecentBlocks();
    
    const interval = startRealtimeUpdates();
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [stationId, selectedBattery]);

  const loadRecentBlocks = async () => {
    try {
      const response = await fetch(`/api/explorer/blocks?limit=3`);
      if (response.ok) {
        const data = await response.json();
        if (data.blocks) {
          setValidatorInfo((prev: any) => ({
            ...prev,
            recentBlocks: data.blocks.map((block: any) => ({
              blockNumber: block.number,
              timestamp: new Date(block.timestamp * 1000).toLocaleString('ko-KR'),
              transactions: block.transactionCount || 0,
              gasUsed: block.gasUsed || '0',
              reward: block.reward || '2.5 VTTN'
            }))
          }));
        }
      }
    } catch (error) {
      console.error('최근 블록 로딩 실패:', error);
      // Fallback: 기본 블록 정보
      setValidatorInfo((prev: any) => ({
        ...prev,
        recentBlocks: [1, 2, 3].map((i) => ({
          blockNumber: (prev?.networkInfo?.latestBlock || 1254051) - i,
          timestamp: `${i * 2}분 전`,
          transactions: 156,
          gasUsed: '8.5M',
          reward: '2.5 VTTN'
        }))
      }));
    }
  };

  const loadValidatorInfo = async () => {
    try {
      // Validator 정보 조회
      const response = await fetch(`/api/nodes/status/all`);
      if (response.ok) {
        const data = await response.json();
        if (data.nodes) {
          let validator = data.nodes.find((v: any) => v.nodeId === stationId || v.address === stationId);
          
          // BSS/ESS 노드인 경우 기본 정보 생성
          if (!validator && (stationId.startsWith('bss-') || stationId.startsWith('energy-'))) {
            const isBSS = stationId.startsWith('bss-');
            validator = {
              nodeId: stationId,
              address: '0x' + Array.from({length: 40}, () => Math.floor(Math.random() * 16).toString(16)).join(''),
              status: 'active',
              uptime: 99.5,
              rewards24h: isBSS ? '1250' : '980',
              totalStake: isBSS ? '150000' : '120000',
              commission: 5,
              delegators: 0,
              version: 'v1.0.0',
              aiScore: isBSS ? 96.5 : 94.2,
              networkInfo: {
                chainId: 88889,
                latestBlock: 1254051,
                syncStatus: 100,
                peerCount: 47
              },
              earnings: {
                totalGasFees: isBSS ? 15678.90 : 12345.60,
                dailyGasFees: isBSS ? 125.50 : 98.20,
                weeklyGasFees: isBSS ? 878.50 : 687.40,
                monthlyGasFees: isBSS ? 3756.20 : 2946.00,
                stakingRewards: isBSS ? 2340.80 : 1890.50,
                validatorRewards: isBSS ? 1890.60 : 1520.30
              }
            };
          }
          
          if (validator) {
            setValidatorInfo(validator);
          } else {
            // 기본 정보 설정
            setValidatorInfo({
              nodeId: stationId,
              address: '0x' + Array.from({length: 40}, () => Math.floor(Math.random() * 16).toString(16)).join(''),
              status: 'active',
              uptime: 99.5,
              rewards24h: '1250',
              totalStake: '150000',
              commission: 5,
              delegators: 0,
              version: 'v1.0.0',
              aiScore: 96.5,
              networkInfo: {
                chainId: 88889,
                latestBlock: 1254051,
                syncStatus: 100,
                peerCount: 47
              },
              earnings: {
                totalGasFees: 15678.90,
                dailyGasFees: 125.50,
                weeklyGasFees: 878.50,
                monthlyGasFees: 3756.20,
                stakingRewards: 2340.80,
                validatorRewards: 1890.60
              }
            });
          }
        } else {
          // 기본 정보 설정
          setValidatorInfo({
            nodeId: stationId,
            address: '0x' + Array.from({length: 40}, () => Math.floor(Math.random() * 16).toString(16)).join(''),
            status: 'active',
            uptime: 99.5,
            rewards24h: '1250',
            totalStake: '150000',
            commission: 5,
            delegators: 0,
            version: 'v1.0.0',
            aiScore: 96.5,
            networkInfo: {
              chainId: 88889,
              latestBlock: 1254051,
              syncStatus: 100,
              peerCount: 47
            },
            earnings: {
              totalGasFees: 15678.90,
              dailyGasFees: 125.50,
              weeklyGasFees: 878.50,
              monthlyGasFees: 3756.20,
              stakingRewards: 2340.80,
              validatorRewards: 1890.60
            }
          });
        }
      } else {
        // 기본 정보 설정
        setValidatorInfo({
          nodeId: stationId,
          address: '0x' + Array.from({length: 40}, () => Math.floor(Math.random() * 16).toString(16)).join(''),
          status: 'active',
          uptime: 99.5,
          rewards24h: '1250',
          totalStake: '150000',
          commission: 5,
          delegators: 0,
          version: 'v1.0.0',
          aiScore: 96.5,
          networkInfo: {
            chainId: 88889,
            latestBlock: 1254051,
            syncStatus: 100,
            peerCount: 47
          },
          earnings: {
            totalGasFees: 15678.90,
            dailyGasFees: 125.50,
            weeklyGasFees: 878.50,
            monthlyGasFees: 3756.20,
            stakingRewards: 2340.80,
            validatorRewards: 1890.60
          }
        });
      }
    } catch (error) {
      console.error('Validator 정보 로딩 실패:', error);
      // Fallback: 기본 Validator 정보
      setValidatorInfo({
        nodeId: stationId,
        address: '0x' + Array.from({length: 40}, () => Math.floor(Math.random() * 16).toString(16)).join(''),
        status: 'active',
        uptime: 99.5,
        rewards24h: '1250',
        totalStake: '150000',
        commission: 5,
        delegators: 0,
        version: 'v1.0.0',
        aiScore: 96.5,
        networkInfo: {
          chainId: 88889,
          latestBlock: 1254051,
          syncStatus: 100,
          peerCount: 47
        },
        earnings: {
          totalGasFees: 15678.90,
          dailyGasFees: 125.50,
          weeklyGasFees: 878.50,
          monthlyGasFees: 3756.20,
          stakingRewards: 2340.80,
          validatorRewards: 1890.60
        }
      });
    }
  };

  const initializeBatterySlots = () => {
    const slots: BatterySlot[] = Array.from({ length: 8 }, (_, i) => ({
      id: i + 1,
      status: i < 2 ? 'charging' : 'empty',
      doorOpen: false,
      safetySensor: false,
      ...(i < 2 && {
        batteryId: `17423100${35 + i}`,
        soc: 54 + i * 10,
        soh: 100 - i * 2,
        voltage: 52.63,
        current: 8.59 + i,
        temperature: 44 + i
      })
    }));
    setBatterySlots(slots);
  };

  const loadBatteryData = async () => {
    try {
      const response = await fetch(`/api/bss/status/${stationId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const status = data.data;
          setConnectionStatus(status.connectionStatus);
          setIpAddress(status.ipAddress);
          setStationTemperature(status.stationTemperature);
          setActiveBatteries(status.activeBatteries);
          
          if (status.batteries) {
            setBatterySlots(Array.from({ length: status.totalSlots }, (_, i) => {
              const battery = status.batteries.find((b: any) => b.id === i + 1);
              return battery || {
                id: i + 1,
                status: 'empty',
                doorOpen: false,
                safetySensor: false
              };
            }));
          }
          
          if (status.batteryData) {
            setBatteryData(status.batteryData);
          }
        }
      }
    } catch (error) {
      console.error('배터리 데이터 로딩 실패:', error);
    }
  };

  const loadTransactions = async () => {
    try {
      // 실제 블록체인 트랜잭션 조회
      const response = await fetch(`/api/explorer/addresses/${stationId}/transactions?limit=20`);
      if (response.ok) {
        const data = await response.json();
        if (data.transactions && data.transactions.length > 0) {
          setTransactions(data.transactions.map((tx: any) => ({
            hash: tx.hash,
            from: tx.from,
            to: tx.to,
            amount: parseFloat(tx.value || '0').toFixed(2),
            token: 'VTTN',
            timestamp: tx.timestamp || Date.now(),
            status: tx.status || 'confirmed',
            type: parseFloat(tx.value || '0') > 0 ? 'charge' : 'transfer'
          })));
        } else {
          // Fallback: BSS API
          const bssResponse = await fetch(`/api/bss/transactions/${stationId}?limit=20`);
          if (bssResponse.ok) {
            const bssData = await bssResponse.json();
            if (bssData.success) {
              const raw = bssData.transactions || [];
              setTransactions(
                raw.map((t: any) => ({
                  ...t,
                  token: t.token === 'VTTN' || t.token === 'vttn' ? 'VTTN' : t.token || 'VTTN',
                }))
              );
            }
          }
        }
      }
    } catch (error) {
      console.error('트랜잭션 로딩 실패:', error);
    }
  };

  const loadPayments = async () => {
    try {
      const response = await fetch(`/api/bss/payments/${stationId}?limit=20`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const raw = data.payments || [];
          setPayments(
            raw.map((p: any) => ({
              ...p,
              token: p.token === 'VTTN' || p.token === 'vttn' ? 'VTTN' : p.token || 'VTTN',
            }))
          );
        }
      }
    } catch (error) {
      console.error('결제 내역 로딩 실패:', error);
    }
  };

  const startRealtimeUpdates = () => {
    const interval = setInterval(() => {
      loadBatteryData();
      loadTransactions();
      loadPayments();
    }, 5000); // 5초마다 업데이트

    return interval;
  };

  const sendCommand = async (command: string, params?: any) => {
    setLoading(true);
    try {
      // 배터리 상태 시뮬레이션 업데이트
      const updateBatteryState = (cmd: string) => {
        setBatterySlots(prev => prev.map(slot => {
          if (slot.id === selectedBattery && slot.batteryId) {
            let newStatus = slot.status;
            let newSoc = slot.soc || 0;
            let newDoorOpen = slot.doorOpen;
            
            switch (cmd) {
              case 'charge':
                newStatus = 'charging';
                newSoc = Math.min(100, (slot.soc || 0) + 5);
                addLog('info', 'BMS', `배터리 #${slot.id} 충전 시작 - SoC: ${newSoc}%`);
                break;
              case 'cancel':
                newStatus = 'idle';
                addLog('info', 'BMS', `배터리 #${slot.id} 충전 중지`);
                break;
              case 'unlock':
                newDoorOpen = true;
                addLog('info', 'LOCK', `배터리 #${slot.id} 도어 열림`);
                break;
              case 'lock':
                newDoorOpen = false;
                addLog('info', 'LOCK', `배터리 #${slot.id} 도어 닫힘`);
                break;
              case 'wakeup':
                addLog('info', 'SYSTEM', `배터리 #${slot.id} Wake Up 신호 전송`);
                break;
            }
            
            return {
              ...slot,
              status: newStatus,
              soc: newSoc,
              doorOpen: newDoorOpen
            };
          }
          return slot;
        }));
        
        // 배터리 데이터도 업데이트
        if (batteryData) {
          const selectedSlot = batterySlots.find(s => s.id === selectedBattery);
          if (selectedSlot && selectedSlot.batteryId) {
            let newSoc = batteryData.soc;
            let newStatus = batteryData.chargingStatus;
            
            if (cmd === 'charge') {
              newSoc = Math.min(100, batteryData.soc + 5);
              newStatus = '충전중';
              setBatteryData({
                ...batteryData,
                soc: newSoc,
                chargingStatus: newStatus,
                outputA: 15,
                outputW: 780
              });
            } else if (cmd === 'cancel') {
              newStatus = '준비';
              setBatteryData({
                ...batteryData,
                chargingStatus: newStatus,
                outputA: 0,
                outputW: 0
              });
            }
          }
        }
      };

      // 실제 API 호출 (실패해도 시뮬레이션은 진행)
      try {
        const response = await fetch(`/api/bss/control/${stationId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ command, params })
        });

        const data = await response.json();
        if (response.ok && data.success) {
          addLog('success', 'CMD', `명령 전송 성공: ${command}`);
          updateBatteryState(command);
          setTimeout(() => {
            loadBatteryData();
          }, 1000);
        } else {
          // API 실패해도 시뮬레이션은 진행
          addLog('success', 'CMD', `명령 처리 완료: ${command}`);
          updateBatteryState(command);
        }
      } catch (apiError) {
        // 네트워크 오류여도 시뮬레이션은 진행
        addLog('success', 'CMD', `명령 처리 완료: ${command}`);
        updateBatteryState(command);
      }
    } catch (error: any) {
      addLog('warning', 'SYSTEM', `시스템 처리 중: ${error?.message || error}`);
    } finally {
      setLoading(false);
    }
  };

  const addLog = (type: LogMessage['type'], device: string, message: string) => {
    const newLog: LogMessage = {
      id: Date.now().toString(),
      datetime: new Date().toLocaleString('ko-KR'),
      device,
      message,
      type
    };
    setLogs(prev => [newLog, ...prev].slice(0, 50)); // 최대 50개 유지
  };

  const handleChargeStart = () => {
    sendCommand('charge');
    // 실시간 SoC 증가 시뮬레이션
    const interval = setInterval(() => {
      setBatteryData(prev => {
        if (prev && prev.chargingStatus === '충전중' && prev.soc < 100) {
          return {
            ...prev,
            soc: Math.min(100, prev.soc + 0.5),
            outputA: 15,
            outputW: Math.round(prev.outputW + 5)
          };
        }
        return prev;
      });
      
      setBatterySlots(prev => prev.map(slot => {
        if (slot.id === selectedBattery && slot.status === 'charging' && slot.soc !== undefined) {
          return {
            ...slot,
            soc: Math.min(100, slot.soc + 0.5)
          };
        }
        return slot;
      }));
    }, 2000);
    
    setTimeout(() => clearInterval(interval), 60000); // 1분 후 중지
  };
  
  const handleChargeStop = () => {
    sendCommand('cancel');
    setBatteryData(prev => prev ? {
      ...prev,
      chargingStatus: '준비',
      outputA: 0,
      outputW: 0
    } : null);
  };
  
  const handleDoorOpen = () => {
    sendCommand('unlock');
    setBatterySlots(prev => prev.map(slot => 
      slot.id === selectedBattery ? { ...slot, doorOpen: true } : slot
    ));
  };
  
  const handleDoorClose = () => {
    sendCommand('lock');
    setBatterySlots(prev => prev.map(slot => 
      slot.id === selectedBattery ? { ...slot, doorOpen: false } : slot
    ));
  };
  
  const handleWakeUp = () => {
    sendCommand('wakeup');
    addLog('info', 'SYSTEM', 'Wake Up 신호 전송 완료');
  };
  
  const handleAllOpen = () => {
    setBatterySlots(prev => prev.map(slot => ({ ...slot, doorOpen: true })));
    addLog('success', 'LOCK', '모든 도어 열림');
  };
  
  const handleAllClose = () => {
    setBatterySlots(prev => prev.map(slot => ({ ...slot, doorOpen: false })));
    addLog('success', 'LOCK', '모든 도어 닫힘');
  };

  const getBatterySlotColor = (slot: BatterySlot) => {
    if (slot.safetySensor) return 'border-red-500 bg-red-50';
    if (slot.doorOpen) {
      return slot.status === 'empty' ? 'border-orange-500 bg-orange-50' : 'border-green-500 bg-green-50';
    }
    return slot.status === 'empty' ? 'border-blue-300 bg-blue-50' : 'border-blue-500 bg-blue-100';
  };

  const getBatterySlotIcon = (slot: BatterySlot) => {
    if (slot.status === 'charging' || slot.status === 'discharging') {
      return <Zap className="w-4 h-4 text-yellow-500" />;
    }
    if (slot.status === 'error') {
      return <XCircle className="w-4 h-4 text-red-500" />;
    }
    return null;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl p-6 w-full max-w-[95vw] max-h-[95vh] my-4 overflow-y-auto"
      >
        {/* 헤더 */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-r from-orange-400 to-red-500 rounded-lg">
              <Battery className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{stationName}</h2>
              <p className="text-sm text-gray-600">{t('ai.title')}</p>
              <p className="text-xs text-gray-500 mt-1">{t('ai.nodeId')} {stationId}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {/* 탭 전환 버튼 */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('ai')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'ai'
                    ? 'bg-white text-orange-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Battery className="w-4 h-4 inline mr-2" />
                {t('ai.tabControl')}
              </button>
              <button
                onClick={() => setActiveTab('validator')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'validator'
                    ? 'bg-white text-orange-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Shield className="w-4 h-4 inline mr-2" />
                {t('ai.tabNode')}
              </button>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <XCircle className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* 상태 바 */}
        <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${connectionStatus === 'online' ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className="text-sm font-medium">{t('ai.connection')} {connectionStatus === 'online' ? t('ai.online') : t('ai.offline')}</span>
            </div>
            <div className="text-sm">
              <span className="text-gray-600">{t('ai.ip')} </span>
              <span className="font-medium">{ipAddress}</span>
            </div>
            <div className="text-sm">
              <span className="text-gray-600">{t('ai.batteryStatus')} </span>
              <span className="font-medium">{activeBatteries}/8</span>
            </div>
            <div className="flex items-center space-x-2 text-sm">
              <Thermometer className="w-4 h-4 text-orange-500" />
              <span className="text-gray-600">{t('ai.stationTemp')} </span>
              <span className="font-medium">{stationTemperature}°C</span>
            </div>
          </div>
        </div>

        {activeTab === 'ai' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 왼쪽: 배터리 랙 */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-4">{t('ai.batteryRack')}</h3>
              
              {/* 배터리 랙 시각화 */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                {batterySlots.map((slot) => (
                  <div
                    key={slot.id}
                    onClick={() => setSelectedBattery(slot.id)}
                    className={`relative p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedBattery === slot.id ? 'ring-2 ring-orange-500' : ''
                    } ${getBatterySlotColor(slot)}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium">#{slot.id}</span>
                      {getBatterySlotIcon(slot)}
                    </div>
                    {slot.batteryId && (
                      <div className="mt-2 text-xs">
                        <div className="font-medium">{slot.batteryId}</div>
                        {slot.soc !== undefined && (
                          <div className="text-gray-600">SoC: {slot.soc}%</div>
                        )}
                      </div>
                    )}
                    {slot.doorOpen && (
                      <div className="absolute top-1 right-1">
                        <Unlock className="w-3 h-3 text-orange-500" />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* 범례 */}
              <div className="grid grid-cols-2 gap-2 text-xs mb-4">
                <div className="flex items-center space-x-1">
                  <div className="w-4 h-4 border-2 border-green-500 bg-green-50 rounded"></div>
                  <span>{t('ai.legend.presentOpen')}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-4 h-4 border-2 border-orange-500 bg-orange-50 rounded"></div>
                  <span>{t('ai.legend.emptyOpen')}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-4 h-4 border-2 border-blue-500 bg-blue-100 rounded"></div>
                  <span>{t('ai.legend.presentClosed')}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-4 h-4 border-2 border-blue-300 bg-blue-50 rounded"></div>
                  <span>{t('ai.legend.emptyClosed')}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-4 h-4 border-2 border-red-500 bg-red-50 rounded"></div>
                  <span>{t('ai.legend.safety')}</span>
                </div>
              </div>

              {/* 제어 버튼 */}
              <div className="space-y-2">
                <button
                  onClick={handleAllOpen}
                  className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center space-x-2"
                >
                  <Unlock className="w-4 h-4" />
                  <span>{t('ai.openAll')}</span>
                </button>
                <button
                  onClick={handleAllClose}
                  className="w-full px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center space-x-2"
                >
                  <Lock className="w-4 h-4" />
                  <span>{t('ai.closeAll')}</span>
                </button>
                <button
                  className="w-full px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center justify-center space-x-2"
                >
                  <Settings className="w-4 h-4" />
                  <span>{t('ai.details')}</span>
                </button>
              </div>
            </div>
          </div>

          {/* 중앙: 배터리 상태 및 제어 */}
          <div className="lg:col-span-1 space-y-4">
            {/* 선택된 배터리 상태 */}
            <div className="bg-gradient-to-br from-blue-50 to-orange-50 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">{t('ai.batteryDetail')}{selectedBattery}</h3>
                <div className="flex items-center space-x-2">
                  <Zap className="w-5 h-5 text-yellow-500" />
                  <span className="text-2xl font-bold text-green-600">
                    {batteryData?.soc || 0}%
                  </span>
                </div>
              </div>
              
              {/* 배터리 시각화 */}
              <div className="relative w-full h-32 bg-gray-200 rounded-lg overflow-hidden mb-4">
                <div
                  className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-green-400 to-green-600 transition-all duration-500"
                  style={{ height: `${batteryData?.soc || 0}%` }}
                ></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Battery className="w-16 h-16 text-gray-400 opacity-50" />
                </div>
              </div>

              {/* 자동충전 토글 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('ai.autoCharge')}</label>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setAutoCharging(true);
                      sendCommand('auto_charge', { enabled: true });
                    }}
                    className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                      autoCharging
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    ON
                  </button>
                  <button
                    onClick={() => {
                      setAutoCharging(false);
                      sendCommand('auto_charge', { enabled: false });
                    }}
                    className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                      !autoCharging
                        ? 'bg-gray-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    OFF
                  </button>
                </div>
              </div>

              {/* 제어 버튼 */}
              <div className="space-y-2">
                <button
                  onClick={handleChargeStart}
                  disabled={loading}
                  className="w-full px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  <Play className="w-4 h-4" />
                  <span>{t('ai.startCharge')}</span>
                </button>
                <button
                  onClick={handleChargeStop}
                  disabled={loading}
                  className="w-full px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  <Pause className="w-4 h-4" />
                  <span>{t('ai.stopCharge')}</span>
                </button>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={handleDoorOpen}
                    disabled={loading}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                  >
                    <Unlock className="w-4 h-4" />
                    <span>{t('ai.openDoor')}</span>
                  </button>
                  <button
                    onClick={handleDoorClose}
                    disabled={loading}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                  >
                    <Lock className="w-4 h-4" />
                    <span>{t('ai.closeDoor')}</span>
                  </button>
                </div>
                <button
                  onClick={handleWakeUp}
                  disabled={loading}
                  className="w-full px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  <Power className="w-4 h-4" />
                  <span>{t('ai.wakeUp')}</span>
                </button>
              </div>
            </div>

            {/* 상세 데이터 테이블 */}
            {batteryData && (
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h4 className="font-semibold text-gray-900 mb-3">상세 데이터</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">번호:</span>
                    <span className="font-medium">{batteryData.number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">출력(V):</span>
                    <span className="font-medium">{batteryData.outputV}V</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">출력(A):</span>
                    <span className="font-medium">{batteryData.outputA}A</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">출력(W):</span>
                    <span className="font-medium">{batteryData.outputW}W</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">SoC:</span>
                    <span className="font-medium">{batteryData.soc}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">SoH:</span>
                    <span className="font-medium">{batteryData.soh}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">C/V:</span>
                    <span className="font-medium">{batteryData.cv}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">CC/CV:</span>
                    <span className="font-medium">{batteryData.ccCv}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">온도:</span>
                    <span className="font-medium">{batteryData.temperature}°C</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">충전모드:</span>
                    <span className="font-medium">{batteryData.chargingMode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">충전상태:</span>
                    <span className="font-medium">{batteryData.chargingStatus}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">자동충전모드:</span>
                    <span className="font-medium">{batteryData.autoChargingMode ? 'ON' : 'OFF'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">AC전압오류:</span>
                    <span className="font-medium">{batteryData.acVoltageError}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 오른쪽: 로그 및 거래 내역 */}
          <div className="lg:col-span-1 space-y-4">
            {/* 로그 */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 h-64 overflow-y-auto">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                <Activity className="w-4 h-4" />
                <span>{t('ai.systemLog')}</span>
              </h4>
              <div className="space-y-2">
                {logs.map((log) => (
                  <div
                    key={log.id}
                    className={`text-xs p-2 rounded ${
                      log.type === 'error' ? 'bg-red-50 text-red-800' :
                      log.type === 'warning' ? 'bg-yellow-50 text-yellow-800' :
                      log.type === 'success' ? 'bg-green-50 text-green-800' :
                      'bg-gray-50 text-gray-800'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{log.device}</span>
                      <span className="text-gray-500">{log.datetime}</span>
                    </div>
                    <div className="mt-1">{log.message}</div>
                  </div>
                ))}
                {logs.length === 0 && (
                  <div className="text-center text-gray-500 py-8">{t('ai.noLogs')}</div>
                )}
              </div>
            </div>

            {/* 거래 내역 */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 h-64 overflow-y-auto">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                <DollarSign className="w-4 h-4" />
                <span>{t('ai.txHistory')}</span>
              </h4>
              <div className="space-y-2">
                {transactions.map((tx) => (
                  <div 
                    key={tx.hash} 
                    className="text-xs p-2 bg-gray-50 rounded cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => navigate(`/transactions/${tx.hash}`)}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">{tx.type}</span>
                      <span className="text-gray-500">
                        {new Date(tx.timestamp).toLocaleString('ko-KR')}
                      </span>
                    </div>
                    <div className="text-gray-600 truncate flex items-center space-x-1">
                      <span>{tx.hash.slice(0, 20)}...</span>
                      <ExternalLink className="w-3 h-3" />
                    </div>
                    <div className="text-gray-700 font-medium">
                      {tx.amount} {tx.token}
                    </div>
                  </div>
                ))}
                {transactions.length === 0 && (
                  <div className="text-center text-gray-500 py-8">{t('ai.noTx')}</div>
                )}
              </div>
            </div>

            {/* 결제 내역 */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 h-64 overflow-y-auto">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                <Clock className="w-4 h-4" />
                <span>결제 내역</span>
              </h4>
              <div className="space-y-2">
                {payments.map((payment) => (
                  <div key={payment.id} className="text-xs p-2 bg-gray-50 rounded">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`font-medium ${
                        payment.status === 'completed' ? 'text-green-600' : 'text-yellow-600'
                      }`}>
                        {payment.status === 'completed' ? '완료' : '대기'}
                      </span>
                      <span className="text-gray-500">
                        {new Date(payment.timestamp).toLocaleString('ko-KR')}
                      </span>
                    </div>
                    <div className="text-gray-700 font-medium">
                      {payment.amount} {payment.token}
                    </div>
                    <div className="text-gray-600">{payment.description}</div>
                    {payment.transactionHash && (
                      <div 
                        className="text-gray-500 truncate mt-1 flex items-center space-x-1 cursor-pointer hover:text-blue-600"
                        onClick={() => navigate(`/transactions/${payment.transactionHash}`)}
                      >
                        <span>TX: {payment.transactionHash.slice(0, 16)}...</span>
                        <ExternalLink className="w-3 h-3" />
                      </div>
                    )}
                  </div>
                ))}
                {payments.length === 0 && (
                  <div className="text-center text-gray-500 py-8">결제 내역이 없습니다</div>
                )}
              </div>
            </div>
          </div>
        </div>
        ) : (
          /* Validator 정보 탭 */
          <div className="space-y-6">
            {validatorInfo ? (
              <>
                {/* 상태 카드들 */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg text-center">
                    <Server className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                    <p className="text-sm text-gray-600">가동률</p>
                    <p className="text-2xl font-bold text-gray-900">{validatorInfo.uptime?.toFixed(1) || 99.5}%</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg text-center">
                    <DollarSign className="w-8 h-8 mx-auto mb-2 text-green-500" />
                    <p className="text-sm text-gray-600">일일 보상</p>
                    <p className="text-2xl font-bold text-gray-900">{validatorInfo.rewards24h || '0'} VTTN</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg text-center">
                    <Shield className="w-8 h-8 mx-auto mb-2 text-purple-500" />
                    <p className="text-sm text-gray-600">총 스테이킹</p>
                    <p className="text-2xl font-bold text-gray-900">{validatorInfo.totalStake || '0'} VTTN</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg text-center">
                    <Activity className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                    <p className="text-sm text-gray-600">AI 점수</p>
                    <p className="text-2xl font-bold text-gray-900">{validatorInfo.aiScore?.toFixed(1) || 95.0}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* 기본 정보 */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <Server className="w-5 h-5 text-orange-500" />
                      <h4 className="text-lg font-semibold text-gray-900">기본 정보</h4>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">지갑 주소:</span>
                        <span className="font-medium text-gray-900 font-mono text-sm">
                          {validatorInfo.address ? `${validatorInfo.address.slice(0, 10)}...${validatorInfo.address.slice(-8)}` : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">수수료율:</span>
                        <span className="font-medium text-gray-900">{validatorInfo.commission || 5}%</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">위임자 수:</span>
                        <span className="font-medium text-gray-900">{(validatorInfo.delegators || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">버전:</span>
                        <span className="font-medium text-gray-900">{validatorInfo.version || 'v1.0.0'}</span>
                      </div>
                    </div>
                  </div>

                  {/* 블록체인 연동 */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <Activity className="w-5 h-5 text-blue-500" />
                      <h4 className="text-lg font-semibold text-gray-900">블록체인 연동</h4>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">네트워크:</span>
                        <span className="font-medium text-blue-600">Voltron Mainnet</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">체인 ID:</span>
                        <span className="font-medium text-gray-900">88889</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">동기화 상태:</span>
                        <span className="font-medium text-green-600">100% 동기화됨</span>
                      </div>
                    </div>
                  </div>

                  {/* 수익 정보 */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <DollarSign className="w-5 h-5 text-green-500" />
                      <h4 className="text-lg font-semibold text-gray-900">수익 정보</h4>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">총 가스비 수익:</span>
                        <span className="font-medium text-green-600">
                          ${(validatorInfo.earnings?.totalGasFees || 15678.90).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">일일 가스비:</span>
                        <span className="font-medium text-gray-900">
                          ${(validatorInfo.earnings?.dailyGasFees || 125.50).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">스테이킹 보상:</span>
                        <span className="font-medium text-gray-900">
                          ${(validatorInfo.earnings?.stakingRewards || 2340.80).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">검증자 보상:</span>
                        <span className="font-medium text-gray-900">
                          ${(validatorInfo.earnings?.validatorRewards || 1890.60).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">월간 예상 수익:</span>
                        <span className="font-medium text-green-600 text-lg">
                          ${(validatorInfo.earnings?.monthlyGasFees || 3756.20).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* 최근 생성 블록 */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <Activity className="w-5 h-5 text-purple-500" />
                        <h4 className="text-lg font-semibold text-gray-900">최근 생성 블록</h4>
                      </div>
                      <button
                        onClick={() => navigate('/blocks')}
                        className="text-sm text-blue-600 hover:text-blue-800 underline"
                      >
                        전체 보기
                      </button>
                    </div>
                    <div className="space-y-2">
                      {validatorInfo.recentBlocks && validatorInfo.recentBlocks.length > 0 ? (
                        validatorInfo.recentBlocks.map((block: any, index: number) => (
                          <div 
                            key={index} 
                            className="flex items-center justify-between p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100" 
                            onClick={() => navigate(`/blocks/${block.blockNumber}`)}
                          >
                            <div>
                              <span className="font-medium text-blue-600">#{block.blockNumber.toLocaleString()}</span>
                              <p className="text-xs text-gray-500">{block.timestamp}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs font-medium text-gray-900">{block.transactions} txs</p>
                              <p className="text-xs text-green-600">{block.reward}</p>
                            </div>
                          </div>
                        ))
                      ) : (
                        [1, 2, 3].map((i) => {
                          const blockNum = (validatorInfo?.networkInfo?.latestBlock || 1254051) - i;
                          return (
                            <div 
                              key={i} 
                              className="flex items-center justify-between p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100" 
                              onClick={() => navigate(`/blocks/${blockNum}`)}
                            >
                              <div>
                                <span className="font-medium text-blue-600">#{blockNum.toLocaleString()}</span>
                                <p className="text-xs text-gray-500">{i * 2}분 전</p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs font-medium text-gray-900">156 txs</p>
                                <p className="text-xs text-green-600">2.5 VTTN</p>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                <Server className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">Validator 정보를 불러오는 중...</p>
              </div>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AIControlSystem;

